import moment from "moment";
import React from "react";
import { Card, Badge } from "react-bootstrap";
import {
  DocuClipperBillingCycle,
  DocuClipperPlan,
  DocuClipperPricing,
  StripePlan,
  StripeSubscription,
  ContractAddonsAttributes,
  SubscriptionPreview,
  SubscriptionItemAttributes,
  GetDiscountsResponse,
} from "../../../../docuclipper/DocuclipperTypes";
import PopoverHelp from "../../../Docuclipper/PopoverHelp";
import { getCurrencySymbol } from "./PlanSelector2";
import { getParentPlanName } from "./tierUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import { ReduxState } from "../../../../redux";

type Props = {
  subscription: StripeSubscription;
  billingCycle: DocuClipperBillingCycle;
  pricing: DocuClipperPricing;
  discountedPricing: DocuClipperPricing | null;
  plan: DocuClipperPlan;
  renderButton: () => JSX.Element;
  plans: StripePlan[];
  addons: SubscriptionItemAttributes[];
  subscriptionPreview: SubscriptionPreview | null;
  discounts?: GetDiscountsResponse | null;
  includeDiscounts?: boolean;
  discountsLoading?: boolean;
};

// Normalize segment values to categories
const normalizeSegment = (segment: string | undefined): string => {
  if (!segment) return "general";

  const normalized = segment.toLowerCase();

  // Accountants
  if (normalized.includes("accountant") || normalized.includes("bookkeeper")) {
    return "accountants";
  }

  // Financial Investigators
  if (
    normalized.includes("financial investigator") ||
    normalized.includes("forensic")
  ) {
    return "financial_investigators";
  }

  // Lenders
  if (
    normalized.includes("lender") ||
    normalized.includes("underwriter") ||
    normalized.includes("credit analyst")
  ) {
    return "lenders";
  }

  // Legal
  if (normalized.includes("legal") || normalized.includes("family law")) {
    return "legal";
  }

  // Business Owners
  if (
    normalized.includes("business owner") ||
    normalized.includes("founder") ||
    normalized.includes("in-house finance")
  ) {
    return "business_owners";
  }

  // Developers
  if (normalized.includes("developer") || normalized.includes("api")) {
    return "developers";
  }

  // Personal/Individual
  if (
    normalized.includes("individual") ||
    normalized.includes("personal use")
  ) {
    return "personal";
  }

  return "general";
};

// Get segment-specific assurances
const getSegmentAssurances = (segment: string | undefined): string[] => {
  const segmentKey = normalizeSegment(segment);

  const assurances = {
    accountants: [
      "Trusted by accounting firms",
      "SOC2 Type II compliant",
      "Bank-grade security & encryption",
    ],
    financial_investigators: [
      "Trusted by investigators worldwide",
      "SOC2 Type II compliant",
      "Bank-grade security & encryption",
    ],
    lenders: [
      "Trusted by lending institutions",
      "SOC2 Type II compliant",
      "Bank-grade security & encryption",
    ],
    legal: [
      "Trusted by law firms worldwide",
      "SOC2 Type II compliant",
      "Bank-grade security & encryption",
    ],
    business_owners: [
      "Trusted by businesses worldwide",
      "SOC2 Type II compliant",
      "Bank-grade security & encryption",
    ],
    developers: [
      "Trusted by development teams",
      "SOC2 Type II compliant",
      "Bank-grade security & encryption",
    ],
    personal: [
      "Trusted by thousands of users",
      "SOC2 Type II compliant",
      "Bank-grade security & encryption",
    ],
    general: [
      "Trusted by customers worldwide",
      "SOC2 Type II compliant",
      "Bank-grade security & encryption",
    ],
  };

  return assurances[segmentKey] || assurances.general;
};

export const PlanSummary: React.FC<Props> = ({
  subscription,
  pricing,
  discountedPricing,
  billingCycle,
  plan,
  renderButton,
  plans,
  addons,
  subscriptionPreview,
  discounts = null,
  includeDiscounts = false,
  discountsLoading = false,
}) => {
  const { user } = useSelector((state: ReduxState) => state.User);
  let proration;
  const maybeProration = subscriptionPreview?.lines.filter((x) =>
    x.description.toLowerCase().includes("unused")
  );
  if (maybeProration && maybeProration?.length > 0) {
    proration = maybeProration[0].amount;
  }

  // Find the actual plan from the plans array to get correct pricing
  const actualPlan = plans.find(
    (p) =>
      p.docuclipperPlan.name === plan &&
      p.docuclipperPlan.billingCycle === billingCycle
  );

  // Determine which plan to look up in pricing object
  // For tier plans like "Starter 60", we need to use the parent plan name
  let planKeyForPricing = plan;

  // If plans array is provided, try to find the parent plan
  if (plans && plans.length > 0) {
    const matchingPlan = plans.find((p) => p.docuclipperPlan.name === plan);
    if (matchingPlan) {
      // Use parent plan name if available, otherwise extract from name
      const parentName = getParentPlanName(matchingPlan);
      if (parentName && pricing[parentName]) {
        planKeyForPricing = parentName as DocuClipperPlan;
      }
    }
  }

  // If the plan key doesn't exist in pricing, try to extract parent plan name
  if (!pricing[planKeyForPricing]) {
    // Try to extract parent from name (e.g., "Starter 60" -> "Starter")
    const match = plan.match(/^(Starter|Business|Enterprise|Professional)/);
    if (match && pricing[match[1]]) {
      planKeyForPricing = match[1] as DocuClipperPlan;
    }
  }

  // console.log({ pricing, plan, billingCycle, actualPlan });
  // Use actual plan pricing if found, otherwise fall back to pricing object
  const amount = actualPlan
    ? Math.round(actualPlan.amount / 100)
    : Math.round(pricing[planKeyForPricing][billingCycle].amount);

  // Apply discount to actual plan amount if we have an actual plan, otherwise use discountedPricing
  const getDiscounted = (originalPrice: number) => {
    if (!subscription?.coupon?.percent_off) {
      return originalPrice;
    }
    return Math.round((originalPrice * (100 - subscription.coupon.percent_off)) / 100);
  };

  const discountedAmount = actualPlan
    ? getDiscounted(amount)
    : discountedPricing
    ? Math.round(discountedPricing[planKeyForPricing][billingCycle].amount)
    : amount;
  // const amountJsx = discountedAmount ? (
  //   <>
  //     <del className="text-muted">{`$${discountedAmount}`}</del>
  //     <span className="ml-2">{`$${amount}`}</span>
  //   </>
  // ) : (
  //   <>
  //     <span>{amount}</span>
  //   </>
  // );
  const currencySymbol = actualPlan
    ? getCurrencySymbol(actualPlan.currency)
    : getCurrencySymbol(pricing[planKeyForPricing][billingCycle].currency);
  // const amountJsx = (
  //   <>{`${currencySymbol}${subscriptionPreview?.total || 0}`}</>
  // );
  const billingCycleStr = billingCycle === "Annually" ? "year" : "month";
  const x = plans.filter((p) => p.docuclipperPlan.name === plan);
  const pages = x.length > 0 ? x[0].docuclipperPlan.numPages : 0;
  const isAnnual = billingCycle === "Annually";
  const annualPages = pages * 12;

  // Calculate final price with discounts
  const calculateFinalPrice = () => {
    if (!discounts || !discounts.discounts.length) {
      return discountedAmount;
    }

    let basePrice = discountedAmount;
    let totalDiscount = 0;
    discounts.discounts.forEach((discount) => {
      if (discount.type === "credit") {
        totalDiscount += discount.amount;
      } else if (discount.type === "percentage") {
        totalDiscount += Math.round((basePrice * discount.amount) / 100);
      }
    });

    return Math.max(0, basePrice - totalDiscount);
  };

  // Calculate annual savings
  const calculateAnnualSavings = () => {
    if (
      billingCycle !== "Annually" ||
      !discounts ||
      !discounts.discounts.length
    ) {
      return 0;
    }

    // Calculate monthly price
    const monthlyPrice = pricing[planKeyForPricing]["Monthly"]?.amount || 0;
    if (!monthlyPrice) return 0;

    const annualMonthlyEquivalent = monthlyPrice * 12;
    const finalAnnualPrice = calculateFinalPrice();

    return Math.max(0, annualMonthlyEquivalent - finalAnnualPrice);
  };

  // Render discount breakdown
  const renderDiscountBreakdown = () => {
    if (!includeDiscounts || !discounts || !discounts.discounts.length) {
      return null;
    }

    const basePrice = discountedAmount;
    const annualSavings = calculateAnnualSavings();

    return (
      <div className="discount-breakdown mb-3 p-3 border rounded">
        <h6 className="mb-3">💰 Available Discounts</h6>

        {/* Original price with strikethrough */}
        <div className="mb-3">
          <div className="d-flex justify-content-between">
            <span>Original Price:</span>
            <span className="text-muted">
              <del>
                {currencySymbol}
                {basePrice}
              </del>
            </span>
          </div>
        </div>

        {/* Each discount on separate lines */}
        {discounts.discounts.map((discount, index) => (
          <div key={discount.id || index} className="mb-3">
            <div className="d-flex justify-content-between text-success">
              <span className="font-weight-medium">{discount.name}</span>
              <span>
                -{currencySymbol}
                {discount.type === "credit"
                  ? discount.amount
                  : Math.round((basePrice * discount.amount) / 100)}
              </span>
            </div>
            {discount.description && (
              <div className="mt-1">
                <small className="text-muted">{discount.description}</small>
              </div>
            )}
          </div>
        ))}

        <hr className="my-3" />

        {/* Final price */}
        <div className="d-flex justify-content-between font-weight-bold h5">
          <span>Final Price:</span>
          <span className="text-success">
            {currencySymbol}
            {calculateFinalPrice()}
          </span>
        </div>

        {/* Annual savings */}
        {annualSavings > 0 && (
          <div className="mt-2">
            <div className="d-flex justify-content-between text-primary">
              <small className="font-weight-medium">💡 Annual Savings:</small>
              <small className="font-weight-bold">
                Save {currencySymbol}
                {annualSavings}/year vs monthly billing
              </small>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Update the amount display based on discounts
  const finalAmount =
    includeDiscounts && discounts && discounts.discounts.length > 0
      ? calculateFinalPrice()
      : discountedAmount;
  const finalAmountJsx = <>{`${currencySymbol}${finalAmount}`}</>;
  const amountJsx = finalAmountJsx;

  return (
    <Card style={{ maxWidth: "350px" }}>
      <Card.Body className="p-4">
        <h4>{`DocuClipper ${plan === "Starter100" ? "Lite" : plan} plan`}</h4>
        {subscription.status !== "trialing" &&
          subscription.docuclipperPlan?.name &&
          subscription.docuclipperPlan.name !== plan && (
            <div className="text-muted small mb-2">
              Changing from{" "}
              <strong>{subscription.docuclipperPlan.name}</strong> to{" "}
              <strong>{plan === "Starter100" ? "Lite" : plan}</strong>
            </div>
          )}
        {!includeDiscounts || !discounts || !discounts.discounts.length ? (
          <div className="text-muted my-2">
            {amountJsx}
            {` / ${billingCycleStr}`}
            <span>{`${
              subscription && subscription.coupon
                ? ` (${subscription.coupon.percent_off}% off regular price)`
                : ""
            }`}</span>
          </div>
        ) : (
          <></>
        )}

        {/* Discount breakdown */}
        {includeDiscounts && (
          <div className="mb-3">
            {discountsLoading && (
              <div className="text-center p-3">
                <span>Loading discounts...</span>
              </div>
            )}
            {!discountsLoading && renderDiscountBreakdown()}
          </div>
        )}
        <div>
          <ul className="list-unstyled">
            {pages && (
              <li className="mb-2">
                <strong>📄 Page Allocation:</strong>
                {isAnnual ? (
                  <>
                    <div className="ml-3">
                      <Badge variant="success" className="mr-2">
                        Annual Benefit
                      </Badge>
                      <strong>{annualPages.toLocaleString()} pages</strong>{" "}
                      available immediately
                    </div>
                    <div className="text-success small ml-3">
                      Use your pages anytime throughout the year!
                    </div>
                  </>
                ) : (
                  <>
                    <div className="ml-3">
                      <strong>{pages.toLocaleString()} pages</strong> per month
                    </div>
                    <div className="text-primary small ml-3">
                      💡 Save up to 30%: switch to annual billing and get all{" "}
                      {annualPages.toLocaleString()} pages available now.
                    </div>
                  </>
                )}
              </li>
            )}
            <li className="mb-2">
              <strong>🛠 Support Level:</strong>
              <div className="ml-3">
                {plan.toLowerCase().includes("business") ||
                plan.toLowerCase().includes("enterprise") ? (
                  <>
                    <span>{`Premium support. Includes dedicated account manager.`}</span>
                    {/* <PopoverHelp
                      placement="right"
                      id="premiumSupportHelp"
                      header=""
                    >
                      <span>Includes dedicated support manager</span>
                    </PopoverHelp> */}
                  </>
                ) : (
                  "Email support"
                )}
              </div>
            </li>
            {isAnnual && (
              <li className="mb-2">
                <strong>💰 Annual Benefits:</strong>
                <ul className="small mt-1">
                  <li>All pages available upfront</li>
                  <li>Maximum flexibility in usage</li>
                  <li>Better cost efficiency</li>
                </ul>
              </li>
            )}
          </ul>
        </div>
        {/* Trust Badges & Assurances */}
        <div className="mb-3 p-3 bg-light rounded">
          <div className="text-center mb-3">
            <div className="d-flex justify-content-around align-items-center mb-2">
              <div className="text-center">
                <FontAwesomeIcon
                  icon="shield-alt"
                  className="text-success"
                  style={{ fontSize: "24px" }}
                />
                <div className="small mt-1" style={{ fontSize: "10px" }}>
                  SSL Secure
                </div>
              </div>
              <div className="text-center">
                <FontAwesomeIcon
                  icon="lock"
                  className="text-primary"
                  style={{ fontSize: "24px" }}
                />
                <div className="small mt-1" style={{ fontSize: "10px" }}>
                  Encrypted
                </div>
              </div>
              <div className="text-center">
                <FontAwesomeIcon
                  icon="star"
                  className="text-warning"
                  style={{ fontSize: "24px" }}
                />
                <div className="small mt-1" style={{ fontSize: "10px" }}>
                  4.8/5 Rating
                </div>
              </div>
            </div>
          </div>

          <hr className="my-2" />

          <div className="small">
            {getSegmentAssurances(user?.segment).map((assurance, idx) => (
              <div key={idx} className="mb-1 d-flex align-items-start">
                <FontAwesomeIcon
                  icon="check-circle"
                  className="text-success mr-2 mt-1"
                  style={{ fontSize: "12px" }}
                />
                <span style={{ color: "#495057" }}>{assurance}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-2 pt-2 border-top">
            <small className="text-success font-weight-bold">
              <FontAwesomeIcon icon="check-circle" className="mr-1" />
              Cancel anytime
            </small>
          </div>
        </div>

        <div className="alert alert-info py-2 px-3 mb-3 small text-center">
          You will be charged <strong>{amountJsx}</strong> today.
        </div>
        {renderButton && renderButton()}
        <p className="text-center text-muted small mt-2 mb-0">
          <FontAwesomeIcon icon="lock" className="mr-1" />
          Secure checkout. Cancel anytime.
        </p>
      </Card.Body>
    </Card>
  );
};
export default PlanSummary;
